import { paramCase } from 'change-case'
import { AssetReference } from '@opallabs/cassini'
import { kuiperClassNames } from '@opallabs/kuiper'
import { doNothing } from '../../helpers/functions/doNothing'
import { useValidModules } from '../../helpers/hooks'
import {
  GetContentByIDV2Return,
  LinkWithData,
  OnAddSectionProps,
  OnLinkDataUpdate,
  OnShowAssetPickerProps,
  Module,
  ModuleDefinition,
} from '../../helpers/types'
import { LiveTweet } from '../LiveTweet'
import { TwitterGif } from '../TwitterGif'
import { TwitterPoll } from '../TwitterPoll'
import { TwitterRetweet } from '../TwitterRetweet'
import { TwitterTweet } from '../TwitterTweet'
import { TwitterVideo } from '../TwitterVideo'
import { Stream } from './Stream'
import { baseStyles } from './styles'

interface Props {
  accountImage: AssetReference | null
  accountName: string
  accountUsername: string
  display: string
  getContentByIdV2?: (id: number) => Promise<GetContentByIDV2Return>
  isPresenting: boolean
  linksWithData: LinkWithData[]
  modules: Module[]
  momentDescription: string
  momentHeaderAsset: AssetReference | null
  momentTitle: string
  onAddSection?: (args: OnAddSectionProps) => void
  onDeleteSection?: (indexToRemove: number) => void
  onDragDrop?: (e: React.DragEvent<HTMLDivElement>) => void
  onExpandAsset?: (assetId: number) => void
  onRemoveAsset?: (assetId: number) => void
  onShowAssetPicker?: (args: OnShowAssetPickerProps) => void
  postType: string
  readOnly: boolean
  scheduledAt: string
  timezone: string
  updateCoverAsset?: (
    assetData: FileList,
    assetId: number,
    index: number
  ) => void
  updateLinkData?: OnLinkDataUpdate
  updateSectionPostOption?: (index: number, value: string, name: string) => void
  updateText?: (val: string, dest: string) => void
  uploadAssets?: (assets: FileList, args: OnShowAssetPickerProps) => void
}

export const availableModules: ModuleDefinition[] = [
  {
    component: TwitterTweet,
    componentName: 'twitter_moment_image',
    title: 'Image',
  },
  {
    component: TwitterGif,
    componentName: 'twitter_moment_gif',
    title: 'GIF',
  },
  {
    component: TwitterVideo,
    componentName: 'twitter_moment_video',
    title: 'Video',
  },
  {
    component: TwitterTweet,
    componentName: 'twitter_moment_link_preview',
    title: 'Link Preview',
  },
  {
    component: TwitterPoll,
    componentName: 'twitter_moment_poll',
    title: 'Poll',
  },
  {
    component: TwitterRetweet,
    componentName: 'twitter_moment_retweet',
    title: 'Repost',
  },
  {
    component: LiveTweet,
    componentName: 'twitter_moment_live_tweet',
    title: 'Live Post',
  },
]

export const TwitterMoment: React.FC<Props> = ({
  accountImage,
  accountName,
  accountUsername,
  display,
  getContentByIdV2,
  isPresenting,
  linksWithData,
  modules,
  momentDescription,
  momentHeaderAsset,
  momentTitle,
  onAddSection,
  onDeleteSection,
  onDragDrop,
  onExpandAsset,
  onRemoveAsset,
  onShowAssetPicker,
  postType,
  readOnly,
  scheduledAt,
  timezone,
  updateCoverAsset,
  updateLinkData,
  updateText,
  updateSectionPostOption,
  uploadAssets,
}) => {
  const validModules = useValidModules({
    modules,
    onAddSection: onAddSection ? onAddSection : doNothing,
    minModulesLength: 0,
    componentNames: [
      'twitter_moment_image',
      'twitter_moment_gif',
      'twitter_moment_video',
      'twitter_moment_link_preview',
      'twitter_moment_poll',
      'twitter_moment_retweet',
      'twitter_moment_live_tweet',
    ],
  })

  return (
    <div
      css={baseStyles(display)}
      className={kuiperClassNames(
        'twitter twitter-moment',
        `twitter-${paramCase(display)}`
      )}
    >
      <Stream
        accountImage={accountImage}
        accountName={accountName}
        accountUsername={accountUsername}
        asset={momentHeaderAsset}
        availableModules={availableModules}
        description={momentDescription || ''}
        display={display}
        getContentByIdV2={getContentByIdV2}
        isPresenting={isPresenting}
        linksWithData={linksWithData}
        modules={validModules}
        onAddSection={onAddSection ? onAddSection : doNothing}
        onDeleteSection={onDeleteSection ? onDeleteSection : doNothing}
        onDescriptionChange={
          updateText
            ? (val: string) => updateText(val, 'moment_description')
            : doNothing
        }
        onDragDrop={onDragDrop ? onDragDrop : doNothing}
        onExpandAsset={onExpandAsset ? onExpandAsset : doNothing}
        onRemoveAsset={onRemoveAsset ? onRemoveAsset : doNothing}
        onShowAssetPicker={onShowAssetPicker ? onShowAssetPicker : doNothing}
        onTitleChange={
          updateText
            ? (val: string) => updateText(val, 'moment_title')
            : doNothing
        }
        postType={postType}
        readOnly={readOnly}
        scheduledAt={scheduledAt}
        timezone={timezone}
        title={momentTitle || ''}
        updateCoverAsset={updateCoverAsset ? updateCoverAsset : doNothing}
        updateLinkData={updateLinkData ? updateLinkData : doNothing}
        updateSectionPostOption={
          updateSectionPostOption ? updateSectionPostOption : doNothing
        }
        uploadAssets={uploadAssets ? uploadAssets : doNothing}
      />
    </div>
  )
}
